/** Configuration options for the ngx-google-tag-manager library. */
export interface IModuleOptions {
    /** A set of dimensions that will be sent with every push. If they aren't populated before the push, they will be set to `undefined`. */
    defaultDimensions?: ICustomDimensions;
    /**
   * By default, as an extra precaution, this library attempts to filter out potential PII sent to Tag Manager.
   * These settings can be used to enable or disable those filters.
   */
    filters?: {
    /**
     * Filter query strings out of information sent to Tag Manager.
     * **This should not be disabled unless the data is being filtered/removed elsewhere.**
     **/
        query?: boolean;
        /**
     * Filter emails out of information sent to Tag Manager.
     * **This should not be disabled unless the data is being filtered elsewhere.**
     **/
        email?: boolean;
        /**
     * Filter phone numbers strings out of information sent to Tag Manager.
     * **This should not be disabled unless the data is being filtered elsewhere.**
     **/
        phone?: boolean;
    };
}

/** Information about the visitor and group they belong to. */
export interface IGroupVisitorDimensions {
    /** Optionally, the group the visitor belongs to. E.g. Their main practice. This is the equivalent of the Pendo `account` property. */
    visitor_group?: IVisitorGroup;
    /** Without a visitor, some services may create an anonymous user. This is the equivalent of the Pendo `visitor` property. */
    visitor_details?: IVisitorDetails;
    /**
   * Optionally, the group which is the parent of the visitor's group. This could be an organization such as a DSO. In pendo this is the
   * equivalent of the `parentAccount`.
   */
    visitor_parent_group?: IVisitorParentGroup;
}

/** A grouping of visitors, such as all the visitors from a practice. */
export interface IVisitorGroup {
    /** E.g. Practice ID. This should likely be unique, even across environments. */
    id: string;
    /** E.g. Practice Name */
    name: string;
    /** Any other key/value pair can be attached, which services such as pendo can accept as entity metadata. */
    [key: string]: any;
}

/** A grouping of visitors, such as all the visitors from a practice. */
export interface IVisitorParentGroup {
    /** E.g. Organization ID. This should likely be unique, even across environments. */
    id: string;
    /** E.g. Practice Name */
    name: string;
    /** Any other key/value pair can be attached, which services such as pendo can accept as entity metadata. */
    [key: string]: any;
}

/** A *single* user. */
export interface IVisitorDetails {
    /**
   * The user's ID number. Cannot contain PII such as the user's name or email. This should be unique, even across environments meaning
   * it may be necessary to append something to this value.
   */
    id: string;
    /** The user's email/username. */
    email: string;
    /** ISO language code. Will likely be set automatically by anything digesting this data. */
    language?: string;
    /** The guardian/patient/user full name. */
    name: string;
    /**
   * The current practice name. We really shouldn't be tracking this here, but we do. What if somebody is in two practices.
   * That's why the concept of an `Account` exists.
   */
    practicename?: string;
    /** The user's security role. */
    role: string;
    /** This could actually contain anything else you like. */
    [key: string]: any;
}

/** A set of dimensions where the key represents a data layer variable name. */
export interface ICustomDimensions { [key: string]: any }

/** The default Universal Analytics event dimension names. */
export interface IDefaultEventDimensions extends ICustomDimensions {
    /** The string that will appear as the event category. */
    event_category?: string;
    /** The string that will appear as the event label. */
    event_label?: string;
    /** A non-negative integer that will appear as the event value. */
    value?: number;
}


/** A set of the default Universal  Analytics event action names. */
export type DefaultEventNames =
  'add_payment_info' |
  'add_to_cart' |
  'add_to_wishlist' |
  'begin_checkout' |
  'checkout_progress' |
  'generate_lead' |
  'login' |
  'purchase' |
  'refund' |
  'remove_from_cart' |
  'search' |
  'select_content' |
  'set_checkout_option' |
  'share' |
  'sign_up' |
  'view_item' |
  'view_item_list' |
  'view_promotion' |
  'view_search_results';
